import { orderBy, query } from 'firebase/firestore';
import { useCallback, useMemo } from 'react';
import { useCollection } from '@/lib/firestore/hooks';
import { agentsCol, usersCol } from '@/lib/firestore/refs';
import type { LookupOption } from '@/lib/ui/lookup-field';
import type { Agent, PrincipalRef, UserProfile } from '@shared/types';

export function toPrincipal(opt: LookupOption): PrincipalRef {
  return { type: opt.badge === 'AI' ? 'agent' : 'user', id: opt.id, name: opt.label };
}

/** Name search over active users and (optionally) Active agents, for assignment lookups. */
export function usePrincipalSearch({ includeAgents }: { includeAgents: boolean }) {
  const users = useCollection<UserProfile>(useMemo(() => query(usersCol(), orderBy('name')), []), []);
  const agents = useCollection<Agent>(useMemo(() => (includeAgents ? query(agentsCol(), orderBy('name')) : null), [includeAgents]), [includeAgents]);
  return useCallback(
    async (q: string): Promise<LookupOption[]> => {
      const needle = q.trim().toLowerCase();
      if (!needle) return [];
      const people = users.data
        .filter((u) => u.active && u.name.toLowerCase().includes(needle))
        .map<LookupOption>((u) => ({ id: u.uid, label: u.name, sub: u.dept, avatar: u.avatar }));
      const bots = includeAgents
        ? agents.data
            .filter((a) => a.status === 'Active' && (a.name.toLowerCase().includes(needle) || a.role.toLowerCase().includes(needle)))
            .map<LookupOption>((a) => ({ id: a.id, label: a.name, sub: a.role, avatar: a.avatar, badge: 'AI' }))
        : [];
      return [...people, ...bots].slice(0, 12);
    },
    [users.data, agents.data, includeAgents],
  );
}
